import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Activity, Cable, CheckCircle2, Power, RefreshCw,
  ShieldAlert, ToggleLeft, ToggleRight, Loader2, Play
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';

interface Waveshare8CHDashboardProps {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
}

const CHANNELS = Array.from({ length: 8 }, (_, i) => i + 1);

export function Waveshare8CHDashboard({
  clientId,
  token,
  pollingIntervalMs = 5000,
}: Waveshare8CHDashboardProps) {
  const [scanners, setScanners] = useState<any[]>([]);
  const [selectedScannerId, setSelectedScannerId] = useState<string | null>(null);
  const [liveReadings, setLiveReadings] = useState<any[]>([]);
  const [scannerLastSeen, setScannerLastSeen] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [togglingChannel, setTogglingChannel] = useState<number | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [, forceStatusTick] = useState(0);

  // Force re-render every 5 s so the Connected/Offline badge stays accurate
  useEffect(() => {
    const id = setInterval(() => forceStatusTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);

  // ──────────────────────────────────────────────────
  // Load scanners from backend
  // ──────────────────────────────────────────────────
  const loadScanners = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/scanners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load scanners');
      const data = await res.json();
      const list: any[] = data.scanners || [];
      setScanners(list);
      setSelectedScannerId((prev) => {
        if (prev && list.some((s) => s.id === prev)) return prev;
        const ws = list.find((s) => s.name?.toLowerCase().includes('waveshare'));
        return ws?.id ?? list[0]?.id ?? null;
      });
    } catch {
      toast.error('Failed to load scanners');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => { loadScanners(); }, [loadScanners]);

  // ──────────────────────────────────────────────────
  // WebSocket live data
  // ──────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });

    const handleLive = (data: any) => {
      if (!mounted) return;
      if (data.type !== 'plc_live_data' || data.connectionType !== 'scanner') return;

      if (data.plcId) {
        setScannerLastSeen((prev) => ({ ...prev, [data.plcId]: Date.now() }));
      }
      if (selectedScannerId && data.plcId !== selectedScannerId) return;

      setLiveReadings((prev) => {
        const next = [...prev];
        for (const [name, value] of Object.entries(data.values ?? {})) {
          const idx = next.findIndex((r) => r.tag_name === name);
          if (idx !== -1) next[idx] = { ...next[idx], value };
          else next.push({ tag_name: name, value });
        }
        return next;
      });
    };

    ws.on('plc_live_data', handleLive);
    ws.connect();
    return () => {
      mounted = false;
      ws.off('plc_live_data', handleLive);
    };
  }, [clientId, token, pollingIntervalMs, selectedScannerId]);

  // Reset live readings when user switches scanner device
  useEffect(() => { setLiveReadings([]); }, [selectedScannerId]);

  // ──────────────────────────────────────────────────
  // Derived state
  // ──────────────────────────────────────────────────
  const ONLINE_WINDOW = Math.max(30_000, pollingIntervalMs * 3);
  const currentScanner = scanners.find((s) => s.id === selectedScannerId) ?? null;
  const connected = currentScanner
    ? Date.now() - (scannerLastSeen[currentScanner.id] ?? 0) < ONLINE_WINDOW
    : false;

  // DI tags: inputType 102 (FC02 Discrete Inputs)
  const diTags = useMemo(
    () => (currentScanner?.tags ?? []).filter((t: any) => t.inputType === 102 || t.input_type === 102),
    [currentScanner],
  );
  // DO tags: inputType 101 (FC01 Coils)
  const doTags = useMemo(
    () => (currentScanner?.tags ?? []).filter((t: any) => t.inputType === 101 || t.input_type === 101),
    [currentScanner],
  );

  const getChannelValue = (channel: number, type: 'DI' | 'DO'): boolean => {
    const address = channel - 1; // Waveshare: CH1 = address 0
    const tags = type === 'DI' ? diTags : doTags;
    const tag = tags.find((t: any) => t.address === address);
    if (!tag) return false;
    const live = liveReadings.find((r) => r.tag_name === tag.name);
    return live ? Boolean(live.value) : false;
  };

  const activeInputs = CHANNELS.filter((ch) => getChannelValue(ch, 'DI')).length;
  const activeOutputs = CHANNELS.filter((ch) => getChannelValue(ch, 'DO')).length;

  // ──────────────────────────────────────────────────
  // Toggle a single DO coil via bridge
  // ──────────────────────────────────────────────────
  const toggleOutput = async (channel: number) => {
    if (!currentScanner || togglingChannel !== null) return;
    const address = channel - 1;
    const newValue = !getChannelValue(channel, 'DO');
    setTogglingChannel(channel);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/scanners/${currentScanner.id}/coil/${address}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ value: newValue }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? 'Failed to toggle output');
      }
      toast.success(`DO${channel} → ${newValue ? 'ON' : 'OFF'}`);
    } catch (err: any) {
      toast.error(err.message ?? 'Error toggling output');
    } finally {
      setTogglingChannel(null);
    }
  };

  const setAllOutputs = async (state: boolean) => {
    for (const ch of CHANNELS) {
      if (getChannelValue(ch, 'DO') !== state) {
        await toggleOutput(ch);
        await new Promise((r) => setTimeout(r, 220));
      }
    }
  };

  // ──────────────────────────────────────────────────
  // Auto-provision missing DI / DO tags
  // ──────────────────────────────────────────────────
  const provisionWaveshareTags = async () => {
    if (!currentScanner || provisioning) return;
    setProvisioning(true);
    try {
      for (const ch of CHANNELS) {
        const address = ch - 1;
        if (!diTags.find((t: any) => t.address === address)) {
          await fetch(`${API_BASE}/api/admin/scanners/${currentScanner.id}/tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: `DI${ch}`, address, inputType: 102 }),
          });
        }
        if (!doTags.find((t: any) => t.address === address)) {
          await fetch(`${API_BASE}/api/admin/scanners/${currentScanner.id}/tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: `DO${ch}`, address, inputType: 101 }),
          });
        }
      }
      toast.success('Waveshare tags provisioned successfully!');
      await loadScanners();
    } catch {
      toast.error('Failed to provision tags');
    } finally {
      setProvisioning(false);
    }
  };

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <section className="rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Cable className="h-4 w-4 text-emerald-500" />
              Modbus RTU IO Module
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">
              Waveshare 8CH Command Center
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Live Waveshare Modbus RTU IO 8CH — 8 digital inputs (FC02) and 8 digital outputs (FC01/FC05).
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Device picker */}
            <div className="w-56">
              <Select
                value={selectedScannerId ?? undefined}
                onValueChange={setSelectedScannerId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Device" />
                </SelectTrigger>
                <SelectContent>
                  {scanners.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No Devices Configured
                    </SelectItem>
                  )}
                  {scanners.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Connection badge */}
            <div
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
                connected
                  ? 'border-emerald-500/30 bg-emerald-500/10'
                  : 'border-amber-500/30 bg-amber-500/10'
              }`}
            >
              <span
                className={`h-3 w-3 rounded-full ${
                  connected
                    ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.8)]'
                    : 'bg-amber-500'
                }`}
              />
              <div>
                <p className="text-sm font-semibold">
                  {connected ? 'Connected' : 'Offline'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentScanner
                    ? `Slave ${currentScanner.slave_id ?? currentScanner.slaveId} / ${
                        currentScanner.baud_rate ?? currentScanner.baudRate
                      } baud`
                    : 'No device selected'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Missing tags warning ── */}
      {currentScanner && (diTags.length < 8 || doTags.length < 8) && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-amber-500">Missing Standard Tags</p>
              <p className="text-sm text-muted-foreground">
                {diTags.length} / 8 DI tags and {doTags.length} / 8 DO tags found. Click
                "Provision" to automatically create all 16 standard Waveshare tags in the backend.
              </p>
            </div>
            <Button
              onClick={provisionWaveshareTags}
              variant="outline"
              disabled={provisioning}
              className="flex-shrink-0 border-amber-500 text-amber-500 hover:bg-amber-500/10"
            >
              {provisioning ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Provision Auto-Tags
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── KPI cards ── */}
      <section className="grid gap-4 md:grid-cols-4">
        {[
          {
            label: 'Digital Inputs',
            value: `${activeInputs} / 8`,
            icon: Activity,
            tone: 'text-emerald-500',
          },
          {
            label: 'Digital Outputs',
            value: `${activeOutputs} / 8`,
            icon: Power,
            tone: 'text-cyan-500',
          },
          {
            label: 'Poll Cycle',
            value: `${pollingIntervalMs} ms`,
            icon: RefreshCw,
            tone: 'text-violet-500',
          },
          {
            label: 'Safety State',
            value: connected ? 'Live' : 'Offline',
            icon: ShieldAlert,
            tone: 'text-amber-500',
          },
        ].map(({ label, value, icon: Icon, tone }) => (
          <Card key={label}>
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </p>
                <p className="mt-1 text-2xl font-bold">{value}</p>
              </div>
              <Icon className={`h-6 w-6 ${tone}`} />
            </CardContent>
          </Card>
        ))}
      </section>

      {/* ── DI + DO panels ── */}
      <div className="space-y-5">
        {/* Digital Inputs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base">Digital Inputs</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                FC02 — Read-only discrete inputs
              </p>
            </div>
            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-500">
              {activeInputs} ON
            </span>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {CHANNELS.map((ch) => {
                const active = getChannelValue(ch, 'DI');
                return (
                  <motion.div
                    key={`di-${ch}`}
                    initial={false}
                    animate={{ scale: active ? [1, 1.03, 1] : 1 }}
                    transition={{ duration: 0.25 }}
                    className={`rounded-lg border p-4 transition-colors ${
                      active
                        ? 'border-emerald-500/40 bg-emerald-500/10'
                        : 'bg-muted/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">DI{ch}</span>
                      <span
                        className={`h-3 w-3 rounded-full transition-all ${
                          active
                            ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]'
                            : 'bg-muted-foreground/30'
                        }`}
                      />
                    </div>
                    <p
                      className={`mt-3 text-2xl font-bold ${
                        active ? 'text-emerald-500' : 'text-muted-foreground'
                      }`}
                    >
                      {active ? 'ON' : 'OFF'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Input channel {ch}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Digital Outputs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base">Digital Outputs</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                FC01 / FC05 — Coil write (via bridge)
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={togglingChannel !== null}
                onClick={() => setAllOutputs(false)}
              >
                All Off
              </Button>
              <Button
                size="sm"
                disabled={togglingChannel !== null}
                onClick={() => setAllOutputs(true)}
              >
                All On
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {CHANNELS.map((ch) => {
                const active = getChannelValue(ch, 'DO');
                const isToggling = togglingChannel === ch;
                return (
                  <motion.div
                    key={`do-${ch}`}
                    initial={false}
                    animate={{ scale: active ? [1, 1.03, 1] : 1 }}
                    transition={{ duration: 0.25 }}
                    className={`rounded-lg border p-4 transition-colors ${
                      active
                        ? 'border-cyan-500/40 bg-cyan-500/10'
                        : 'bg-muted/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">DO{ch}</span>
                      {active ? (
                        <ToggleRight className="h-5 w-5 text-cyan-500" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <p
                      className={`mt-3 text-2xl font-bold ${
                        active ? 'text-cyan-500' : 'text-muted-foreground'
                      }`}
                    >
                      {active ? 'ON' : 'OFF'}
                    </p>
                    <Button
                      className="mt-4 w-full"
                      variant={active ? 'outline' : 'default'}
                      size="sm"
                      disabled={isToggling || togglingChannel !== null}
                      onClick={() => toggleOutput(ch)}
                    >
                      {isToggling ? (
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      ) : null}
                      {active ? 'Turn Off' : 'Turn On'}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Footer ── */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span>
            Live data is streamed directly from the edge bridge. DO writes are sent as Modbus FC05
            (Write Single Coil) commands through the bridge command queue.
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
